import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { map, switchMap, tap, catchError, filter, take, timeout } from 'rxjs/operators';
import { AllServicesService } from '../../../services/all-services.service';
import { SharedServiceService } from '../shared-service.service';
import { FavoriteParserService } from './favorite-parser.service';
import { FavoriteSerializerService } from './favorite-serializer.service';
import { CalAngularService } from '@cvx/cal-angular';
import {
  FavoriteSlot,
  FavoriteBlendState,
  FAVORITE_SLOT_COUNT,
  createEmptySlots,
} from './favorite.models';

@Injectable({ providedIn: 'root' })
export class FavoriteFacadeService {

  private slotsSubject = new BehaviorSubject<FavoriteSlot[]>(createEmptySlots());
  slots$ = this.slotsSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  private userEmail = '';

  constructor(
    private allServices: AllServicesService,
    private sharedService: SharedServiceService,
    private parser: FavoriteParserService,
    private serializer: FavoriteSerializerService,
    private authService: CalAngularService,
  ) {
    // Get user email from CAL auth and pre-load favorites
    this.authService.isUserSignedIn().subscribe(isSignedIn => {
      if (isSignedIn) {
        const account = this.authService.getAccount();
        this.userEmail = (account?.username || '').toLowerCase();
        // Pre-load favorites in background once we have the email
        if (this.userEmail) {
          this.loadAllFavorites().subscribe();
        }
      }
    });
  }

  /**
   * Load all favorites from blob, filter by current user, populate slots.
   * Only shows loading spinner if cache is empty (first load).
   */
  loadAllFavorites(): Observable<FavoriteSlot[]> {
    const hasCachedData = this.slotsSubject.value.some(s => s.Name !== null);
    if (!hasCachedData) {
      this.loadingSubject.next(true);
    }
    return this.allServices.Favorites_form_blob().pipe(
      map(data => this.mapBlobResponseToSlots(data)),
      tap(slots => {
        this.slotsSubject.next(slots);
        this.loadingSubject.next(false);
      }),
      catchError(err => {
        console.error('Error loading favorites:', err);
        this.loadingSubject.next(false);
        return of(this.slotsSubject.value);
      }),
    );
  }

  /**
   * Save current blend state to a specific slot with a given name.
   */
  saveFavoriteWithName(slotNumber: number, name: string): Observable<{ success: boolean; message: string; payload?: string }> {
    const sanitizedName = this.sanitizeFilename(name);
    if (!sanitizedName) {
      return of({ success: false, message: 'Invalid file name.' });
    }

    return from(this.serializer.buildV1Payload(String(slotNumber), this.buildShareableLinkBase())).pipe(
      switchMap(payload => {
        if (!payload) {
          return of({ success: false, message: 'Cannot save empty data. Please add at least one component.' });
        }
        const csvData = {
          email: this.userEmail,
          blobfilename: sanitizedName,
          tablesData: payload,
        };
        return this.allServices.saveFavorites_to_blob(csvData).pipe(
          map(() => ({ success: true, message: 'Favorite saved successfully.', payload })),
        );
      }),
      catchError(err => {
        console.error('Error saving favorite:', err);
        return of({ success: false, message: 'Error saving favorite.' });
      }),
    );
  }

  /**
   * Save current blend state to a specific slot.
   * Only works if slot is empty (Name === null) and blend data exists.
   */
  saveFavorite(slotNumber: number): Observable<{ success: boolean; message: string }> {
    const slots = this.slotsSubject.value;
    const slot = slots.find(s => s.slno === String(slotNumber));

    if (slot && slot.Name !== null) {
      return of({ success: false, message: 'Data already exists. Please delete it in order to save new data.' });
    }

    return from(this.serializer.buildV1Payload(String(slotNumber), this.buildShareableLinkBase())).pipe(
      switchMap(payload => {
        if (!payload) {
          return of({ success: false, message: 'Cannot save empty data.' });
        }
        const csvData = {
          email: this.userEmail,
          blobfilename: 'Fav' + slotNumber,
          tablesData: payload,
        };
        return this.allServices.saveFavorites_to_blob(csvData).pipe(
          switchMap(() => this.loadAllFavorites()),
          map(() => ({ success: true, message: 'Data saved successfully.' })),
        );
      }),
      catchError(err => {
        console.error('Error saving favorite:', err);
        return of({ success: false, message: 'Error saving favorite.' });
      }),
    );
  }

  /**
   * Import a favorite's blend state into the application.
   */
  importFavorite(slot: FavoriteSlot): { success: boolean; message: string } {
    if (!slot.Name) {
      return { success: false, message: 'This Favorite is empty or deleted.' };
    }

    const blendState = this.parser.parseAny(slot.Name);
    if (!blendState) {
      return { success: false, message: 'Could not parse favorite data.' };
    }

    this.serializer.applyStateFromPayload(blendState);
    this.sharedService.setImportedFavoriteName(slot.UIName);
    this.sharedService.setImportedFavoriteSlot(slot.slno);
    this.sharedService.setImportedFavoriteFilename(slot.Filename);
    return { success: true, message: 'Favorite imported successfully.' };
  }

  /**
   * Load a shared favorite by blob filename (used when opening a /shareddata/:filename link in a
   * new tab). Fetches the single blob, parses it, and applies the state — triggering the same
   * hydrate + auto-predict flow as a normal import.
   */
  loadSharedFavorite(filename: string): Observable<{ success: boolean; message: string }> {
    if (!filename) {
      return of({ success: false, message: 'No favorite specified.' });
    }

    console.log('[SharedFavorite] Fetching favorite blob:', filename);
    return this.allServices.sharedFavorites_form_blob({ filenamereq: filename }).pipe(
      switchMap(resp => {
        if (!resp || resp.message === 'File not found' || !resp.data) {
          console.warn('[SharedFavorite] Blob missing or deleted:', filename, resp);
          return of({ success: false, message: 'This favorite is deleted or unavailable.' });
        }

        const state = this.parser.parseAny(resp.data);
        if (!state) {
          console.error('[SharedFavorite] Failed to parse blob content:', filename);
          return of({ success: false, message: 'Could not parse favorite data.' });
        }

        console.log('[SharedFavorite] Blob loaded (%d bytes). Waiting for dropdown data before applying...', resp.data.length);
        // Gate on dropdown readiness so the modification panels are mounted and hydration
        // has valid options. Prevents the intermittent failure and the stuck "Loading...".
        return this.waitForBlendCalculatorReady().pipe(
          map(() => {
            console.log('[SharedFavorite] Dropdown data ready. Applying favorite state:', filename);
            this.serializer.applyStateFromPayload(state);
            this.sharedService.setImportedFavoriteName(this.extractUIName(filename));
            this.sharedService.setImportedFavoriteFilename(filename);
            return { success: true, message: 'Favorite loaded successfully.' };
          })
        );
      }),
      catchError(err => {
        console.error('[SharedFavorite] Error loading shared favorite:', filename, err);
        return of({ success: false, message: 'Error loading shared favorite.' });
      }),
    );
  }

  /**
   * Resolves once the blend-calculator dropdown data has settled (or after a safety timeout).
   * If dropdowns already loaded, resolves immediately (BehaviorSubject replay).
   */
  private waitForBlendCalculatorReady(): Observable<boolean> {
    return this.sharedService.blendCalculatorReady$.pipe(
      filter(ready => ready === true),
      take(1),
      timeout(15000),
      catchError(() => {
        console.warn('[SharedFavorite] Timed out waiting for dropdown data; applying favorite anyway.');
        return of(true);
      }),
    );
  }

  /**
   * Rename a favorite blob.
   */
  renameFavorite(slot: FavoriteSlot, newName: string): Observable<{ success: boolean; message: string }> {
    if (!slot.Filename) {
      return of({ success: false, message: 'No file to rename.' });
    }

    const sanitizedName = this.sanitizeFilename(newName);
    if (!sanitizedName) {
      return of({ success: false, message: 'Invalid file name.' });
    }

    // Backend stores the blob under newFileName verbatim, so send the full
    // "{email}_{name}" to match the save/list filename convention.
    const newFilename = `${this.userEmail}_${sanitizedName}`;
    const payload = {
      oldFileName: slot.Filename,
      newFileName: newFilename,
      email: this.userEmail,
    };

    return this.allServices.editFavorites_to_blob(payload).pipe(
      tap(() => {
        // Optimistic update: rename locally without reloading from blob
        const slots = this.slotsSubject.value.map(s => {
          if (s.slno === slot.slno) {
            return {
              ...s,
              UIName: sanitizedName,
              Filename: newFilename,
              shareableLink: this.buildShareLink(newFilename),
            };
          }
          return s;
        });
        this.slotsSubject.next(slots);
      }),
      map(() => ({ success: true, message: 'File name updated successfully.' })),
      catchError(err => {
        console.error('Error renaming favorite:', err);
        return of({ success: false, message: 'Error renaming favorite.' });
      }),
    );
  }

  /**
   * Rename a favorite to a name already used by another slot: delete that target
   * blob first, then rename. The renamed favorite keeps the last-picked name.
   */
  overwriteRename(
    sourceSlot: FavoriteSlot,
    targetSlot: FavoriteSlot,
    newName: string
  ): Observable<{ success: boolean; message: string }> {
    if (!sourceSlot.Filename) {
      return of({ success: false, message: 'No file to rename.' });
    }
    if (!this.sanitizeFilename(newName)) {
      return of({ success: false, message: 'Invalid file name.' });
    }
    if (!targetSlot.Filename) {
      return this.renameFavorite(sourceSlot, newName);
    }

    return this.allServices.delete_Favorites_in_blob({ file_Name: targetSlot.Filename }).pipe(
      tap(() => {
        // Free the overwritten target slot locally.
        const slots = this.slotsSubject.value.map(s =>
          s.slno === targetSlot.slno
            ? { slno: s.slno, UIName: null, Filename: null, Name: null, shareableLink: null }
            : s
        );
        this.slotsSubject.next(slots);
      }),
      switchMap(() => this.renameFavorite(sourceSlot, newName)),
      catchError(err => {
        console.error('Error overwriting favorite on rename:', err);
        return of({ success: false, message: 'Error renaming favorite.' });
      }),
    );
  }

  /**
   * Delete a favorite blob.
   */
  deleteFavorite(slot: FavoriteSlot): Observable<{ success: boolean; message: string }> {
    if (!slot.Filename) {
      return of({ success: false, message: 'No file to delete.' });
    }

    const payload = { file_Name: slot.Filename };

    return this.allServices.delete_Favorites_in_blob(payload).pipe(
      tap(() => {
        // Optimistic update: clear slot locally
        const slots = this.slotsSubject.value.map(s => {
          if (s.slno === slot.slno) {
            return { slno: s.slno, UIName: null, Filename: null, Name: null, shareableLink: null };
          }
          return s;
        });
        this.slotsSubject.next(slots);
      }),
      map(() => ({ success: true, message: 'Data successfully deleted.' })),
      catchError(err => {
        console.error('Error deleting favorite:', err);
        return of({ success: false, message: 'Error deleting favorite.' });
      }),
    );
  }

  /**
   * Get a shareable link for a given slot.
   */
  getShareLink(slot: FavoriteSlot): string | null {
    if (!slot.Filename || !slot.UIName) return null;
    return this.buildShareLink(`${this.userEmail}_${slot.UIName}`);
  }

  /**
   * Build a /shareddata deep link, URL-encoding the filename so characters like "%" or spaces
   * survive the round trip and match the stored blob key when Angular decodes the route param.
   */
  private buildShareLink(filename: string): string {
    return `${window.location.origin}/shareddata/${encodeURIComponent(filename)}`;
  }

  /**
   * Get current slots array synchronously (snapshot).
   */
  getSlotsSnapshot(): FavoriteSlot[] {
    return this.slotsSubject.value;
  }

  /**
   * Add or update a slot in the local cache (optimistic).
  * @param content - The actual blob content so the slot can be imported without reload.
   */
  addSlotLocally(slotNumber: string, name: string, content: string): void {
    const filename = this.buildFilename(name);
    const slots = this.slotsSubject.value.map(s => {
      if (s.slno === slotNumber) {
        return {
          slno: slotNumber,
          UIName: name,
          Filename: filename,
          Name: content,
          shareableLink: this.buildShareLink(filename),
        };
      }
      return s;
    });
    this.slotsSubject.next(slots);
  }

  /**
   * Build blob filename from a favorite name.
   */
  buildFilename(name: string): string {
    return `${this.userEmail}_${this.sanitizeFilename(name)}`;
  }

  // --- Private helpers ---

  /**
   * Maps the raw blob API response to FavoriteSlot[].
   * API returns an object: { "email@domain.com_FavX": "blob content", ... }
   * We filter by current user email and extract slot number from content.
   */
  private mapBlobResponseToSlots(data: any): FavoriteSlot[] {
    const slots = createEmptySlots();
    if (!data || !this.userEmail) return slots;

    const entries = (Object.entries(data) as [string, string][])
      .sort(([leftFilename], [rightFilename]) => leftFilename.localeCompare(rightFilename));
    const formatRanks = new Map<string, number>();

    entries.forEach(([filename, blobContent]) => {
      // Only process blobs belonging to this user
      if (!filename.includes(this.userEmail)) return;

      const format = this.parser.detectFormat(blobContent);
      if (format === 'invalid' || !this.parser.parseAny(blobContent)) return;

      // Extract UIName from filename: "email@domain.com_FavName"
      const uiName = this.extractUIName(filename);

      // Extract slot number from content
      const slotNumber = this.extractSlotNumber(blobContent);
      if (!slotNumber) return;

      const slotIdx = parseInt(slotNumber, 10) - 1;
      if (slotIdx < 0 || slotIdx >= FAVORITE_SLOT_COUNT) return;

      const formatRank = format === 'legacy' ? 2 : 1;
      if ((formatRanks.get(slotNumber) || 0) >= formatRank) return;

      slots[slotIdx] = {
        slno: slotNumber,
        UIName: uiName,
        Filename: filename,
        Name: blobContent,
        shareableLink: uiName
          ? this.buildShareLink(`${this.userEmail}_${uiName}`)
          : null,
      };
      formatRanks.set(slotNumber, formatRank);
    });

    return slots;
  }

  /**
   * Extract UIName from blob filename by stripping the known "{email}_" prefix.
   * Prefix-based (not ".com_") so names containing ".com_" or "_" survive intact, and
   * non-".com" email domains parse correctly.
   */
  private extractUIName(filename: string): string | null {
    const prefix = `${this.userEmail}_`;
    if (this.userEmail && filename.startsWith(prefix)) {
      return filename.substring(prefix.length) || null;
    }
    // Fallback: first "_" after the "@domain" boundary, else the first "_".
    const atIdx = filename.indexOf('@');
    const underscore = filename.indexOf('_', atIdx > -1 ? atIdx : 0);
    return underscore > -1 ? filename.substring(underscore + 1) : null;
  }

  /**
   * Extract slot number from blob content.
   * Legacy: first section before "0: $" contains "snum: X"
   * V2 JSON: payload.slotNumber
   */
  private extractSlotNumber(content: string): string | null {
    if (!content) return null;

    const trimmed = content.trim();
    // V2 JSON
    if (trimmed.startsWith('{')) {
      try {
        const obj = JSON.parse(trimmed);
        if (obj.slotNumber) return String(obj.slotNumber);
      } catch { /* fall through */ }
    }

    // Legacy: first section before "0: $"
    const sections = content.split('0: $');
    if (sections.length > 0) {
      const header = sections[0].replace(/,/g, '').replace(/\n/g, '');
      // Format: "snum: 1" or similar
      const match = header.match(/snum:\s*(\d+)/);
      if (match) return match[1];
      // Fallback: just extract digits after "snum:"
      const sliced = header.slice(6).trim();
      if (/^\d+$/.test(sliced)) return sliced;
    }

    return null;
  }

  private buildShareableLinkBase(): string {
    return `${window.location.origin}/shareddata/${this.userEmail}`;
  }

  // URL-unsafe plus invisible/confusable characters forbidden in a favorite name; "%" is kept.
  // Blocks C0/C1 controls, DEL, zero-width, bidi overrides/isolates, BOM and line/paragraph separators.
  private readonly forbiddenNameChars =
    /[:/?#\[\]@!$&'()*+,;=<>"\\^{}|`\u0000-\u001F\u007F-\u009F\u200B-\u200F\u2028-\u202E\u2060-\u2069\uFEFF]/g;

  /**
   * Remove characters that are unsafe in a URL / shareable link.
   * Spaces are kept here (converted to "_" only when building the blob name).
   */
  stripForbiddenNameChars(name: string): string {
    if (!name) return '';
    return name.replace(this.forbiddenNameChars, '');
  }

  /**
   * Public sanitized form of a name (spaces -> "_", forbidden chars removed).
   */
  sanitizeName(name: string): string | null {
    return this.sanitizeFilename(name);
  }

  /**
   * Find an occupied slot whose sanitized name matches `name` (optionally excluding one slot).
   * Sanitizes both sides so display variants (spaces, stripped chars) cannot collide silently.
   */
  findOccupiedSlotByName(name: string, excludeSlno?: string): FavoriteSlot | undefined {
    const target = this.sanitizeFilename(name)?.toLowerCase();
    if (!target) return undefined;
    return this.slotsSubject.value.find(
      s => s.Name !== null && s.slno !== excludeSlno &&
        this.sanitizeFilename(s.UIName || '')?.toLowerCase() === target,
    );
  }

  /**
   * Sanitize a filename: replace spaces with "_", remove reserved chars.
   */
  private sanitizeFilename(name: string): string | null {
    if (!name || !name.trim()) return null;
    let sanitized = name.trim().replace(/\s+/g, '_');
    sanitized = this.stripForbiddenNameChars(sanitized);
    return sanitized.length > 0 ? sanitized : null;
  }
}
